import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Container, Mountain, Calendar, Tag, Check } from 'lucide-react';
import { SERVICES, STATS, NEWS, ADVISORY, VALUE_PROPS, TRADE_LANES, PORTAL_FEATURES, TESTIMONIAL, INDUSTRIES } from '../../content/site';
import { Reveal } from '../shared/Reveal';
import { Button } from '../sv';

const H2 = 'text-[32px] md:text-[40px] sv-display';

export function AdvisoryBar() {
  return (
    <div className="bg-gray-50 border-b border-gray-200" data-testid="advisory-bar">
      <div className="sv-wrap h-11 flex items-center gap-3 text-sm overflow-hidden">
        <span className="shrink-0 text-[12px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{ADVISORY.tag}</span>
        <span className="truncate text-gray-700">{ADVISORY.text}</span>
        <Link to={ADVISORY.href} className="ml-auto shrink-0 sv-ink-link text-sm">Read more <ArrowRight className="h-4 w-4" strokeWidth={1.75} /></Link>
      </div>
    </div>
  );
}

export function QuickLinks() {
  const items = [
    ['Book a container', 'FCL and LCL, 20\' to 40\' high cube', '/booking', Container],
    ['Charter bulk tonnage', 'Handysize to Capesize, by the ton', '/booking?mode=bulk', Mountain],
    ['Point-to-point schedules', '68 trade lanes, fixed weekly days', '/schedules', Calendar],
    ['Tariffs and surcharges', 'Published prices, valid 7 days', '/quote', Tag],
  ];
  return (
    <div className="bg-white border-b border-gray-200" data-testid="quick-links">
      <div className="sv-wrap grid grid-cols-2 lg:grid-cols-4 lg:divide-x divide-gray-200">
        {items.map(function (it) {
          const Icon = it[3];
          return (
            <Link key={it[2]} to={it[2]} className="group flex items-start gap-4 py-7 pr-6 lg:px-6 first:lg:pl-0 text-ink hover:bg-gray-50 transition-colors duration-[120ms]" data-testid={"quick-" + it[2].replace(/[^a-z]/g, '')}>
              <Icon className="h-5 w-5 text-ink shrink-0 mt-0.5" strokeWidth={1.75} />
              <span className="flex-1"><span className="block text-[15px] font-medium">{it[0]}</span><span className="block text-[13px] text-gray-600 mt-0.5">{it[1]}</span></span>
              <ArrowRight className="h-4 w-4 text-gray-600 mt-1 group-hover:translate-x-0.5 transition-transform duration-[120ms]" strokeWidth={1.75} />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function WhySudvik() {
  return (
    <section className="py-24" data-testid="why-section">
      <div className="sv-wrap grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 h-[380px] lg:h-[560px] rounded overflow-hidden bg-gray-200"><img src="/img/quay-walk.jpg" alt="Two Südvik colleagues on a container quay" className="w-full h-full object-cover" /></div>
        <div className="lg:col-span-7 lg:pl-6">
          <p className="sv-kicker">Why shippers choose Südvik</p>
          <h2 className={H2 + ' mt-2 max-w-[600px]'}>A shipping line that behaves like a schedule, not a forecast.</h2>
          <div className="grid gap-8 mt-10">
            {VALUE_PROPS.map(function (v, i) {
              return (
                <Reveal key={v.title} delay={i * 0.05}>
                  <div className="grid grid-cols-[40px_1fr] gap-4">
                    <div className="sv-mono text-sm text-gray-600 pt-1">0{i + 1}</div>
                    <div><h3 className="text-xl font-medium tracking-[-0.01em]">{v.title}</h3><p className="text-gray-700 mt-2 max-w-[560px]">{v.text}</p></div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="flex gap-3 mt-10"><Button to="/about">About Südvik</Button><Button variant="secondary" to="/schedules">See schedule integrity</Button></div>
        </div>
      </div>
    </section>
  );
}

export function Services() {
  return (
    <section className="py-24 bg-gray-50 border-y border-gray-200" data-testid="services-section">
      <div className="sv-wrap">
        <div className="grid lg:grid-cols-12 gap-6 items-end mb-12">
          <div className="lg:col-span-7"><p className="sv-kicker">Services</p><h2 className={H2 + ' mt-2'}>One network for bulk and boxes.</h2></div>
          <p className="lg:col-span-5 text-gray-700 lg:pb-1">Six services, one booking reference. Whether it is a single reefer or a Capesize parcel of iron ore, you get the same tracking, the same documents and the same person to call.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {SERVICES.slice(0, 3).map(function (s, i) {
            return (
              <Reveal key={s.slug} delay={i * 0.06}>
                <Link to={'/services/' + s.slug} className="sv-card block overflow-hidden h-full group hover:border-ink transition-colors duration-[120ms]" data-testid={"service-card-" + s.slug}>
                  <div className="h-[200px] bg-gray-200 overflow-hidden"><img src={s.hero} alt="" className="w-full h-full object-cover" /></div>
                  <div className="p-6">
                    <div className="text-[22px] tracking-[-0.01em]">{s.title}</div>
                    <p className="text-gray-700 mt-2 text-[15px]">{s.short}</p>
                    <span className="sv-ink-link mt-5 text-[15px]">Learn more <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-[120ms]" strokeWidth={1.75} /></span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
        <div className="grid sm:grid-cols-3 gap-x-6 gap-y-3 mt-8 pt-8 border-t border-gray-200">
          {SERVICES.slice(3).map(function (s) {
            return <Link key={s.slug} to={'/services/' + s.slug} className="flex items-center justify-between gap-3 py-3 text-[15px] text-ink hover:text-blue-600 transition-colors duration-[120ms]" data-testid={"service-link-" + s.slug}><span className="flex items-center gap-3"><s.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />{s.title}</span><ArrowRight className="h-4 w-4 text-gray-600" strokeWidth={1.75} /></Link>;
          })}
        </div>
      </div>
    </section>
  );
}

export function StatsBand() {
  return (
    <section className="bg-blue-500 text-ink" data-testid="stats-bar">
      <div className="sv-wrap py-16 md:py-[72px] grid lg:grid-cols-[1.1fr_2fr] gap-12 items-center">
        <div><h2 className={H2}>A network you can set your clock by.</h2><p className="text-[15px] text-ink/80 mt-4 max-w-[380px]">Figures updated weekly from vessel AIS and terminal gate data. Schedule integrity is measured against the published proforma at the discharge port.</p></div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-10">
          {STATS.map(function (s) {
            return <div key={s[1]}><div className="text-[40px] md:text-[48px] font-light leading-none tracking-[-0.02em]">{s[0]}</div><div className="text-[15px] mt-2">{s[1]}</div></div>;
          })}
        </div>
      </div>
    </section>
  );
}

export function TradeLanes() {
  return (
    <section className="py-24" data-testid="trade-lanes">
      <div className="sv-wrap">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div><p className="sv-kicker">Trade lanes</p><h2 className={H2 + ' mt-2'}>Fixed weekday departures on 68 lanes.</h2></div>
          <Button variant="secondary" to="/schedules" iconRight={<ArrowRight className="h-[18px] w-[18px]" strokeWidth={1.75} />} data-testid="lanes-view-all">All schedules</Button>
        </div>
        <div className="sv-card overflow-x-auto">
          <table className="sv-table">
            <thead><tr><th>Lane</th><th>Service</th><th>Typical routing</th><th>Transit</th><th>Departures</th><th></th></tr></thead>
            <tbody>
              {TRADE_LANES.map(function (l) {
                return (
                  <tr key={l.code} data-testid={"lane-" + l.code}>
                    <td className="font-medium whitespace-nowrap">{l.lane}</td>
                    <td className="sv-mono text-sm">{l.code}</td>
                    <td className="whitespace-nowrap">{l.from} → {l.to}</td>
                    <td className="sv-mono text-sm">{l.days} days</td>
                    <td className="text-[15px]">{l.weekly} weekly</td>
                    <td className="text-right"><Link to={'/schedules?origin=' + encodeURIComponent(l.from) + '&destination=' + encodeURIComponent(l.to)} className="sv-ink-link text-[15px]">Sailings <ArrowRight className="h-4 w-4" strokeWidth={1.75} /></Link></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export function BulkSection() {
  return (
    <section className="bg-ink text-white" data-testid="bulk-spotlight">
      <div className="grid lg:grid-cols-2">
        <div className="min-h-[360px] lg:min-h-[560px] bg-gray-700"><img src="/img/sudvik-bulk.jpg" alt="Südvik bulk carrier loading grain at a terminal" className="w-full h-full object-cover" /></div>
        <div className="px-5 md:px-16 py-16 md:py-24 flex flex-col justify-center">
          <p className="text-sm text-white/70">Bulk and break-bulk</p>
          <h2 className={H2 + ' text-white mt-3'}>Tonnage by the metric ton, fixed online.</h2>
          <p className="text-white/75 mt-5 max-w-[520px]">Grain, coal, iron ore, steel or project cargo. Pick the commodity, enter the parcel size and we publish a per-ton rate with a suggested vessel class, from Handysize to Capesize. Loading terms, laycan and stevedoring are agreed in the same booking.</p>
          <ul className="grid gap-3 mt-8 text-[15px]">
            {['Dry bulk and break-bulk on the same platform', 'FIOST, FILO, LIFO and LILO terms', 'Statement of facts and laytime in your portal'].map(function (t) { return <li key={t} className="flex gap-3 text-white/85"><Check className="h-5 w-5 text-blue-400 shrink-0" strokeWidth={1.75} />{t}</li>; })}
          </ul>
          <div className="grid grid-cols-3 gap-6 mt-10 max-w-[520px] border-t border-white/15 pt-8">
            {[['500 MT', 'minimum parcel'], ['4', 'vessel classes'], ['45M t', 'carried last year']].map(function (s) {
              return <div key={s[1]}><div className="text-[32px] font-light leading-none tracking-[-0.02em]">{s[0]}</div><div className="text-sm text-white/60 mt-2">{s[1]}</div></div>;
            })}
          </div>
          <div className="flex flex-wrap gap-3 mt-10">
            <Button variant="brand" to="/quote?mode=bulk" data-testid="bulk-quote-btn">Get bulk prices</Button>
            <Button variant="inverse-ghost" to="/services/bulk-shipping" data-testid="bulk-learn-btn">How it works</Button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PortalSection() {
  return (
    <section className="py-24 bg-gray-50 border-b border-gray-200" data-testid="portal-section">
      <div className="sv-wrap grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <p className="sv-kicker">Compass, the customer portal</p>
          <h2 className={H2 + ' mt-2'}>Your shipping, in one place.</h2>
          <p className="text-gray-700 mt-5 max-w-[520px]">Quote, book, pay and track from a single account. Your regional agent sees the same screen you do, so there is never a second version of the truth.</p>
          <div className="grid gap-5 mt-10">
            {PORTAL_FEATURES.map(function (f) { return <div key={f[0]} className="grid grid-cols-[24px_1fr] gap-3"><Check className="h-5 w-5 text-blue-600 mt-0.5" strokeWidth={1.75} /><div><div className="text-[17px] font-medium">{f[0]}</div><p className="text-gray-700 text-[15px] mt-1">{f[1]}</p></div></div>; })}
          </div>
          <div className="flex gap-3 mt-10"><Button to="/register" data-testid="portal-register-btn">Open an account</Button><Button variant="ghost" to="/login">Log in</Button></div>
        </div>
        <div className="lg:col-span-6 h-[360px] lg:h-[520px] rounded overflow-hidden bg-gray-200"><img src="/img/ops-planner.jpg" alt="Operations planner reviewing a vessel schedule" className="w-full h-full object-cover" /></div>
      </div>
    </section>
  );
}

export function IndustriesStrip() {
  return (
    <section className="py-24" data-testid="industries-section">
      <div className="sv-wrap">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div><p className="sv-kicker">Industries</p><h2 className={H2 + ' mt-2'}>Built around the way your sector ships.</h2></div>
          <Button variant="ghost" to="/industries" iconRight={<ArrowRight className="h-[18px] w-[18px]" strokeWidth={1.75} />}>All industries</Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {INDUSTRIES.map(function (ind) {
            return <Link key={ind.slug} to={'/industries#' + ind.slug} className="sv-card p-5 hover:border-ink transition-colors duration-[120ms]" data-testid={"industry-card-" + ind.slug}><ind.icon className="h-5 w-5 text-ink" strokeWidth={1.75} /><div className="text-[17px] mt-4">{ind.title}</div><p className="text-[13px] text-gray-600 mt-1.5 leading-relaxed">{ind.text}</p></Link>;
          })}
        </div>
      </div>
    </section>
  );
}

export function PeopleSection() {
  return (
    <section className="py-24 border-t border-gray-200" data-testid="people-section">
      <div className="sv-wrap grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 h-[360px] lg:h-[480px] rounded overflow-hidden bg-gray-200"><img src="/img/customer-service.jpg" alt="Südvik customer service specialist" className="w-full h-full object-cover" /></div>
        <div className="lg:col-span-7 lg:pl-6">
          <p className="sv-kicker">Customers</p>
          <blockquote className="text-[26px] md:text-[32px] sv-display mt-3 max-w-[720px]">“{TESTIMONIAL.quote}”</blockquote>
          <div className="mt-6"><div className="text-[15px] font-medium">{TESTIMONIAL.name}</div><div className="text-sm text-gray-600">{TESTIMONIAL.role}</div></div>
          <div className="grid sm:grid-cols-3 gap-6 mt-10 pt-8 border-t border-gray-200">
            {[['1 day', 'account approval'], ['24 h', 'operations desk'], ['121', 'countries with local staff']].map(function (s) { return <div key={s[1]}><div className="text-[28px] font-light leading-none tracking-[-0.01em]">{s[0]}</div><div className="text-sm text-gray-600 mt-2">{s[1]}</div></div>; })}
          </div>
        </div>
      </div>
    </section>
  );
}

export function SustainabilityTeaser() {
  return (
    <section className="relative bg-ink text-white" data-testid="sustainability-teaser">
      <img src="/img/vessel-at-sea.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/30" />
      <div className="sv-wrap relative py-24 md:py-28 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <p className="text-sm text-white/70">Sustainability</p>
          <h2 className={H2 + ' text-white mt-2'}>Net zero by 2040. Measured, published, audited.</h2>
          <p className="text-white/75 mt-5 max-w-[560px]">Twelve methanol dual-fuel vessels on order, an 18% fuel reduction per TEU-km since 2019 and emissions reporting on every booking. The plan is public and so is our progress against it.</p>
          <div className="flex gap-3 mt-8"><Button variant="inverse" to="/sustainability" data-testid="sustainability-btn">Our plan</Button></div>
        </div>
        <div className="lg:col-span-5 grid grid-cols-3 lg:grid-cols-1 gap-6 lg:border-l lg:border-white/15 lg:pl-10 self-center">
          {[['−18%', 'fuel per TEU-km since 2019'], ['12', 'methanol vessels on order'], ['100%', 'of bookings with emissions data']].map(function (s) { return <div key={s[1]}><div className="text-[36px] font-light leading-none tracking-[-0.02em]">{s[0]}</div><div className="text-sm text-white/65 mt-2">{s[1]}</div></div>; })}
        </div>
      </div>
    </section>
  );
}

export function Insights() {
  return (
    <section className="py-24" data-testid="news-section">
      <div className="sv-wrap">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div><p className="sv-kicker">Newsroom</p><h2 className={H2 + ' mt-2'}>Latest from Südvik</h2></div>
          <Button variant="ghost" to="/news" iconRight={<ArrowRight className="h-[18px] w-[18px]" strokeWidth={1.75} />} data-testid="news-view-all">Newsroom</Button>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {NEWS.slice(0, 3).map(function (n, i) {
            return (
              <Reveal key={n.id} delay={i * 0.06}>
                <Link to={'/news#' + n.id} className="block text-ink group" data-testid={"news-card-" + n.id}>
                  <div className="h-[220px] rounded overflow-hidden bg-gray-200"><img src={n.image} alt="" className="w-full h-full object-cover" /></div>
                  <div className="text-sm text-gray-600 mt-4">{n.tag} · {new Date(n.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                  <div className="text-xl tracking-[-0.01em] mt-1.5 leading-[1.3] group-hover:text-blue-600 transition-colors duration-[120ms]">{n.title}</div>
                  <p className="text-[15px] text-gray-700 mt-2">{n.excerpt}</p>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function CtaBand() {
  return (
    <section className="bg-blue-500 text-ink" data-testid="cta-section">
      <div className="sv-wrap py-16 md:py-20 flex flex-wrap items-center justify-between gap-8">
        <div>
          <h2 className={H2}>Rates in two minutes.</h2>
          <p className="text-ink/80 text-lg mt-3">Port-to-port or door-to-door, valid for 7 days. No account required.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" size="lg" to="/contact" data-testid="cta-contact-btn">Talk to sales</Button>
          <Button size="lg" to="/quote" iconRight={<ArrowRight className="h-[18px] w-[18px]" strokeWidth={1.75} />} data-testid="cta-quote-btn">Get a quote</Button>
        </div>
      </div>
    </section>
  );
}
